'use client';

import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Report to /api/log, like every other error boundary already does.
  //
  // This one — which catches the MOST severe failures, the ones that take out
  // the root layout — reported nothing at all. A crash here left no trace on
  // the server: no URL, no stack, no digest. The route-level boundaries all
  // log; the one that matters most was silent.
  //
  // sendBeacon because the page may be tearing down: fetch() can be cancelled
  // mid-flight during navigation, and beacon is queued by the browser.
  // Wrapped because a failure to LOG must never replace the error being shown.
  useEffect(() => {
    try {
      const payload = JSON.stringify({
        level: 'error',
        source: 'client',
        message: 'Global error boundary caught error',
        component: 'GlobalError',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        stack: error.stack,
        // Next.js attaches a digest to server-side errors; it is the only way
        // to correlate this with the matching server log entry.
        metadata: { digest: error.digest, message: error.message },
        timestamp: new Date().toISOString(),
      });

      const blob = new Blob([payload], { type: 'application/json' });
      if (!navigator.sendBeacon?.('/api/log', blob)) {
        void fetch('/api/log', { method: 'POST', body: payload, keepalive: true });
      }
    } catch {
      // Reporting is best-effort; the user still gets the message below.
    }
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: 'system-ui, -apple-system, sans-serif', background: '#0F1419', color: '#E8ECF1' }}>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ textAlign: 'center', maxWidth: '28rem' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Something went wrong</h1>
            <p style={{ fontSize: '0.875rem', color: '#8899AA', marginBottom: '1.5rem' }}>
              {error.message || 'A critical error occurred. Please try again.'}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <button
                id="global-error-retry"
                type="button"
                onClick={() => reset()}
                style={{ padding: '0.625rem 1.25rem', borderRadius: '0.5rem', background: '#D4A843', color: '#000', fontSize: '0.875rem', fontWeight: 700, border: 'none', cursor: 'pointer' }}
              >
                Try Again
              </button>
              {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error replaces the entire React tree; next/link is not available */}
              <a
                id="global-error-home"
                href="/"
                style={{ padding: '0.625rem 1.25rem', borderRadius: '0.5rem', border: '1px solid #2A3A4E', fontSize: '0.875rem', fontWeight: 600, color: '#8899AA', textDecoration: 'none' }}
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
